import type { Cart } from "@graphql/generated/types";

// Amazon Pay places the order server-side in /api/checkout/heap/amazon-pay/return and redirects
// straight to the confirmation page, so no client code runs at authorisation time to fire the
// purchase event. The cart is not persisted by the store (see store.tsx partialize), so we stash
// it here before the shopper leaves for Amazon and consume it on the confirmation page.
export const AMAZON_PAY_PURCHASE_SNAPSHOT_KEY = "amazon_pay_purchase_snapshot";

export const savePurchaseSnapshot = (cart: Cart) => {
    try {
        sessionStorage.setItem(AMAZON_PAY_PURCHASE_SNAPSHOT_KEY, JSON.stringify(cart));
    } catch {
        // Storage can reject (quota, Safari private mode). Losing the analytics event is
        // acceptable; breaking the payment step is not.
    }
};

// Reads and removes in one step: the removal is what stops the polling confirmation page from
// firing a second purchase event for the same order.
export const consumePurchaseSnapshot = (): Cart | null => {
    try {
        const raw = sessionStorage.getItem(AMAZON_PAY_PURCHASE_SNAPSHOT_KEY);
        sessionStorage.removeItem(AMAZON_PAY_PURCHASE_SNAPSHOT_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as Cart;
    } catch {
        return null;
    }
};

const firedKey = (orderNumber: string) => `amazon_pay_purchase_fired:${orderNumber}`;

// Consuming the snapshot guards against a re-render, but not against navigating back to an old
// confirmation page after a later checkout has written a fresh snapshot. This marker does.
export const hasPurchaseEventFired = (orderNumber: string) => {
    try {
        return sessionStorage.getItem(firedKey(orderNumber)) !== null;
    } catch {
        // Can't prove it hasn't fired — inflated revenue is worse than a missing event.
        return true;
    }
};

export const markPurchaseEventFired = (orderNumber: string) => {
    try {
        sessionStorage.setItem(firedKey(orderNumber), "1");
    } catch {
        // See savePurchaseSnapshot.
    }
};
